import ENVIRONMENTS from '../../environments';
import { store } from '../../store/create-store';
import { isQccTestnetHost } from './qcc-env';

export type Hex = `0x${string}`;

export interface MpWeb3State {
  address?: Hex;
  chainId?: number;
  isConnected: boolean;
  isConnecting: boolean;
}

export type MpWeb3ProofResult = 'proven' | 'conflict' | 'failed';

export interface MpWeb3Receipt {
  status: 'success' | 'reverted';
  transactionHash: Hex;
  blockNumber: bigint;
  replaced?: 'repriced' | 'cancelled' | 'replaced';
}

export interface MpWeb3PreparedSend {
  chainId: number;
  from: Hex;
  preparedAt: number;
  send: () => Promise<Hex>;
}

export interface MpWeb3ContractCall {
  address: Hex;
  abi: readonly unknown[];
  functionName: string;
  args?: readonly unknown[];
}

export interface MpWeb3Api {
  mount: (config?: {
    appName?: string;
    enableTestnets?: boolean;
    keycloakRealmUrl?: string;
    getAuthToken?: () => string | null | undefined;
  }) => void;
  open: () => void;
  connect: (options?: { chainId?: number }) => Promise<MpWeb3State>;
  disconnect: () => Promise<void>;
  switchChain: (chainId: number) => Promise<void>;
  isMobile: () => boolean;
  reopenWallet: () => boolean;
  prove: () => Promise<MpWeb3ProofResult>;
  getState: () => MpWeb3State;
  subscribe: (cb: (state: MpWeb3State) => void) => () => void;
  evm: {
    writeContract: (parameters: MpWeb3ContractCall & { chainId?: number }) => Promise<Hex>;
    prepareWriteContract: (parameters: MpWeb3ContractCall & { chainId: number }) => Promise<MpWeb3PreparedSend>;
    waitForReceipt: (hash: Hex, options?: { confirmations?: number }) => Promise<MpWeb3Receipt>;
  };
}

declare global {
  interface Window {
    mpWeb3?: MpWeb3Api;
  }
}

export const PREPARED_SEND_STALE = 'PreparedSendStaleError';
export const PREPARED_SEND_REFRESH_MS = 45 * 1000;
export const USER_REJECTED = 4001;

export function bearerToken(): string | null {
  const { common } = store.getState() as any;
  return common?.auth?.token || new URLSearchParams(window.location.search).get('token');
}

export function sameAddress(a?: string | null, b?: string | null): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

declare const __WEB3_CONNECT_VERSION__: string | undefined;

// The wallet keeps one URL across deploys and the service worker used to cache
// it CacheFirst, so the build's content hash goes on the URL.
export function web3ConnectUrl(
  version: string | undefined = typeof __WEB3_CONNECT_VERSION__ === 'undefined'
    ? undefined
    : __WEB3_CONNECT_VERSION__,
): string {
  const url = `${process.env.PUBLIC_URL || ''}/web3-connect.js`;
  return version ? `${url}?v=${version}` : url;
}

let loadPromise: Promise<MpWeb3Api> | null = null;
let mounted: MpWeb3Api | null = null;

export function loadMpWeb3(): Promise<MpWeb3Api> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<MpWeb3Api>((resolve, reject) => {
    if (window.mpWeb3) {
      resolve(window.mpWeb3);
      return;
    }
    const script = document.createElement('script');
    script.src = web3ConnectUrl();
    script.async = true;
    script.onload = () => {
      if (window.mpWeb3) resolve(window.mpWeb3);
      else reject(new Error('web3-connect.js loaded but window.mpWeb3 is missing.'));
    };
    script.onerror = () => reject(new Error('Failed to load the wallet (web3-connect.js).'));
    document.head.appendChild(script);
  }).then(api => {
    api.mount({
      appName: 'QuickCam',
      enableTestnets: isQccTestnetHost(window.location.hostname),
      keycloakRealmUrl: ENVIRONMENTS.KEYCLOAK_ACCOUNT,
      getAuthToken: bearerToken,
    });
    mounted = api;
    return api;
  });
  loadPromise.catch(() => {
    loadPromise = null;
  });
  return loadPromise;
}

export function mountedMpWeb3(): MpWeb3Api | null {
  return mounted;
}

export function isMobileWallet(): boolean {
  if (mounted) return mounted.isMobile();
  const data = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (typeof data?.mobile === 'boolean') return data.mobile;
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;
  // iPadOS Safari sends a Mac user agent; a Mac reports no touch points.
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

export function reopenWallet(): boolean {
  return mounted?.reopenWallet() ?? false;
}

export function resetMpWeb3ForTests(): void {
  loadPromise = null;
  mounted = null;
}
